/* eslint-disable @typescript-eslint/no-explicit-any */
import { Injectable } from '@angular/core';
import {
  HttpRequest,
  HttpHandler,
  HttpInterceptor,
  HttpErrorResponse,
} from '@angular/common/http';
import { CustomHttpHeaders } from '../models/auth.model';
import { AuthService } from '../services/auth.service';
import { catchError, throwError } from 'rxjs';
import { ClientInfoData } from '../models/general.model';
import { Router } from '@angular/router';
import { API } from '../configs/api.config';
import { environment } from 'projects/sharing/environments/environment';
import { LS } from '../enums/local-storage.enum';
import { HeaderType } from '../enums/http-headers.enum';

const SESSION_EXPIRED_LOGIN_URL = '/auth/login?sessionExpired=true';

const AUTH_SESSION_EXPIRED_MESSAGES = [
  'token is expired',
  'unauthorized: authentication request header is missing.',
  "unauthorized: authentication request header '_access' is missing.",
  'unauthorized: user session not found or expired',
];

function isExactAuthEndpoint(url: string, endpoint: string): boolean {
  const clean = url.split('?')[0];
  return clean === endpoint || clean.endsWith(endpoint);
}

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  constructor(
    private readonly authService: AuthService,
    private readonly router: Router,
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): any {
    const requestAuthGeneration = this.authService.getAuthGeneration();
    const setHeaders = this.setHeaders(request);
    const authRequest = request.clone({ setHeaders });
    return next.handle(authRequest).pipe(
      catchError((error: HttpErrorResponse) => {
        if (
          this.shouldHandleExpiredSession(request, error, requestAuthGeneration)
        ) {
          this.authService.softLogout();
          void this.router.navigateByUrl(SESSION_EXPIRED_LOGIN_URL);
        }

        return throwError(() => error);
      }),
    );
  }

  private shouldHandleExpiredSession(
    request: HttpRequest<any>,
    error: HttpErrorResponse,
    requestAuthGeneration: number,
  ): boolean {
    if (error.status !== 401) {
      return false;
    }

    // Ignore stale responses from a previous authentication lifecycle.
    if (requestAuthGeneration !== this.authService.getAuthGeneration()) {
      return false;
    }

    // Logout endpoint — 401 here means the session is already gone.
    if (isExactAuthEndpoint(request.url, API.USERS_LOGOUT)) {
      return false;
    }

    // Login endpoint — 401 is expected for wrong credentials.
    if (request.url.includes(API.LOGIN)) {
      return false;
    }

    // Nothing to clear when there is no client session.
    if (!this.authService.isAuthenticated()) {
      return false;
    }

    const message: unknown = error.error?.message;
    if (typeof message !== 'string') {
      return false;
    }

    const normalized = message.toLowerCase();
    return AUTH_SESSION_EXPIRED_MESSAGES.some((item) =>
      normalized.includes(item),
    );
  }

  setHeaders(request: HttpRequest<any>): CustomHttpHeaders {
    const clientInfoStr: string | null = localStorage.getItem(LS.CLIENT_INFO);
    let clientInfo: Partial<ClientInfoData> = {
      IP: '-',
      DeviceStr: '-',
      LocationStr: '-',
    };

    if (clientInfoStr) {
      try {
        clientInfo = JSON.parse(clientInfoStr);
      } catch (error) {
        /* empty */
      }
    }
    const existingHeaders = request.headers.keys().reduce((acc, key) => {
      const value = request.headers.get(key);
      if (value !== null) {
        acc[key] = value;
      }
      return acc;
    }, {} as CustomHttpHeaders);
    const setHeaders: CustomHttpHeaders = {
      [HeaderType.X_API_KEY]: environment.X_API_KEY,
      [HeaderType.X_USER_IP]: clientInfo?.IP ?? '-',
      [HeaderType.X_USER_DEVICE]: clientInfo?.DeviceStr ?? '-',
      [HeaderType.X_USER_LOCATION]: clientInfo?.LocationStr ?? '-',
      ...existingHeaders,
    };

    if (request.url.includes('ipinfo')) {
      return {};
    }

    // Custom Authorization override (e.g. MFA / file upload endpoints that
    // carry their own bearer). Don't touch the caller's header.
    if (
      request.headers.get(HeaderType.AUTHORIZATION) &&
      request.headers.get(HeaderType.AUTHORIZATION) !== ''
    ) {
      return setHeaders;
    }

    // The browser attaches the HttpOnly auth cookie automatically, so we
    // no longer read or forward any token from localStorage. The guards
    // own the "unauthenticated → /auth/login" navigation.
    return setHeaders;
  }
}
